import {
  applySubmissionResult,
  beginSubmission,
  createQuote,
  saveDraft,
  transitionQuote,
  updateQuote,
} from '../state/machine';
import {
  createOrderConfirmationSnapshot,
  emptyOrderConfirmationForm,
  validateOrderConfirmationForm,
} from '../state/orderConfirmation';
import { mergeQuoteItemSelections } from '../state/quoteItems';
import {
  negotiationIsExpired,
  publishNegotiation,
  setNegotiationLinkLifecycle,
  submitShareReceipt,
} from '../state/receiptState';
import type {
  CpqState,
  CustomerCatalogRecord,
  QuoteSelectionDevice,
} from '../state/types';
import { createDisposableFieldValuePort } from '../state/unifiedFieldPort';

/** Stable IDs keep the local rehearsal idempotent and separate from user quotes. */
export const LOCAL_DEMO_SCENARIO_QUOTE_IDS = {
  draft: 'LOCAL-DEMO-QUOTE-DRAFT',
  sharedFeedback: 'LOCAL-DEMO-QUOTE-FEEDBACK',
  expiredLink: 'LOCAL-DEMO-QUOTE-EXPIRED-LINK',
  revokedLink: 'LOCAL-DEMO-QUOTE-REVOKED-LINK',
  submissionSuccess: 'LOCAL-DEMO-QUOTE-SUBMIT-SUCCESS',
  submissionFailure: 'LOCAL-DEMO-QUOTE-SUBMIT-FAILURE',
  archived: 'LOCAL-DEMO-QUOTE-ARCHIVED',
} as const;

const SCENARIO_TITLES: Record<
  keyof typeof LOCAL_DEMO_SCENARIO_QUOTE_IDS,
  string
> = {
  draft: '【本地演示】报价草稿（可继续编辑）',
  sharedFeedback: '【本地演示】客户已分享并提交反馈',
  expiredLink: '【本地演示】客户链接已过期',
  revokedLink: '【本地演示】客户链接已撤销',
  submissionSuccess: '【本地演示】确认下单成功（模拟）',
  submissionFailure: '【本地演示】确认下单失败（模拟）',
  archived: '【本地演示】已归档报价',
};

const SCENARIO_CUSTOMER_ID = 'local-demo-scenario-customer';
const SCENARIO_CUSTOMER_NO = 'LOCAL-DEMO-SCENARIO-001';
const SCENARIO_EXPIRED_AT = '2020-01-01T00:00:00.000Z';
const SCENARIO_QUOTE_VALID_UNTIL = '2099-12-31';
const fieldValuePort = createDisposableFieldValuePort();

type NavigationSnapshot = Pick<
  CpqState,
  | 'activeQuoteId'
  | 'activeQuoteVersionId'
  | 'openQuoteIds'
  | 'activeView'
  | 'quoteManagementDetail'
  | 'currentRoleId'
  | 'currentActorUserId'
  | 'role'
>;

function isValidMobile(value: string): boolean {
  return /^1\d{10}$/.test(value);
}

function addScenarioCustomer(state: CpqState): CustomerCatalogRecord {
  const existingDemoCustomer = state.catalog.customers.find(
    (customer) =>
      customer.name.startsWith('本地演示客户') && isValidMobile(customer.phone),
  );
  if (existingDemoCustomer) return existingDemoCustomer;

  const existingReservedCustomer = state.catalog.customers.find(
    (customer) => customer.id === SCENARIO_CUSTOMER_ID,
  );
  if (existingReservedCustomer) {
    if (!isValidMobile(existingReservedCustomer.phone))
      throw new Error('报价演示客户记录的手机号无效，原数据已保留');
    return existingReservedCustomer;
  }

  let phoneSuffix = 2;
  let phone = `138000000${String(phoneSuffix).padStart(2, '0')}`;
  while (state.catalog.customers.some((customer) => customer.phone === phone)) {
    phoneSuffix += 1;
    if (phoneSuffix > 99)
      throw new Error('没有可用的本地演示手机号，无法生成报价场景');
    phone = `138000000${String(phoneSuffix).padStart(2, '0')}`;
  }

  const customer: CustomerCatalogRecord = {
    id: SCENARIO_CUSTOMER_ID,
    customerNo: SCENARIO_CUSTOMER_NO,
    name: '本地演示客户·报价流程',
    contact: '演示联系人',
    phone,
    manager: '本地演示用户',
    managerUserId: state.currentActorUserId,
    status: 'active',
  };
  state.catalog.customers = [...state.catalog.customers, customer];
  return customer;
}

function selectScenarioDevice(state: CpqState): QuoteSelectionDevice {
  const device = state.catalog.devices.find(
    (candidate) =>
      candidate.status === 'active' &&
      Boolean(candidate.sku?.trim()) &&
      typeof candidate.rentalPrice === 'number' &&
      Number.isFinite(candidate.rentalPrice) &&
      candidate.rentalPrice >= 0,
  );
  if (!device) throw new Error('本地设备目录中没有可用于报价演示的有效设备');
  return device;
}

function switchToSeedAdministrator(state: CpqState): CpqState {
  const role = state.catalog.roles.find(
    (candidate) =>
      candidate.key === 'super_admin' && candidate.status === 'active',
  );
  const actor = role
    ? state.catalog.users.find(
        (user) => user.roleId === role.id && user.status === 'active',
      )
    : undefined;
  return role && actor
    ? {
        ...state,
        currentRoleId: role.id,
        currentActorUserId: actor.id,
        role: role.key,
      }
    : state;
}

function createScenarioDraft(
  state: CpqState,
  scenario: keyof typeof LOCAL_DEMO_SCENARIO_QUOTE_IDS,
  customer: CustomerCatalogRecord,
  device: QuoteSelectionDevice,
): { state: CpqState; quoteId: string } {
  const stableQuoteId = LOCAL_DEMO_SCENARIO_QUOTE_IDS[scenario];
  if (state.quotes[stableQuoteId]) return { state, quoteId: stableQuoteId };

  // Avoid overwriting an existing user quote if a stale sequence counter is present.
  const highestQuoteSequence = Object.keys(state.quotes).reduce(
    (highest, id) => {
      const match = /^Q-V2-(\d+)$/.exec(id);
      return match ? Math.max(highest, Number(match[1])) : highest;
    },
    0,
  );
  const prepared = {
    ...state,
    nextSequence: Math.max(state.nextSequence, highestQuoteSequence + 1),
  };
  let next = createQuote(prepared);
  const generatedQuoteId = next.activeQuoteId;
  const generatedQuote = next.quotes[generatedQuoteId];
  if (!generatedQuote) throw new Error('报价演示草稿创建失败');

  const { [generatedQuoteId]: _generatedQuote, ...quotes } = next.quotes;
  next = {
    ...next,
    quotes: {
      ...quotes,
      [stableQuoteId]: {
        ...generatedQuote,
        quoteId: stableQuoteId,
        masterId: stableQuoteId,
        title: SCENARIO_TITLES[scenario],
      },
    },
    openQuoteIds: next.openQuoteIds.map((id) =>
      id === generatedQuoteId ? stableQuoteId : id,
    ),
    activeQuoteId: stableQuoteId,
    audits: next.audits.map((audit) =>
      audit.quoteId === generatedQuoteId
        ? { ...audit, quoteId: stableQuoteId }
        : audit,
    ),
  };

  next = updateQuote(
    next,
    stableQuoteId,
    (quote) => {
      const merged = mergeQuoteItemSelections(
        quote.items,
        [
          {
            device,
            source: {
              sourceType: 'device',
              sourceId: device.id,
              sourceName: device.name,
              ...(device.equipmentCode
                ? { sourceCode: device.equipmentCode }
                : {}),
            },
            sourceRevisionId: device.currentRevisionId,
            quantity: 1,
          },
        ],
        next.nextSequence,
        quote.businessModeCode,
      );
      return {
        ...quote,
        customer: {
          customerId: customer.id,
          managerUserId: customer.managerUserId,
          name: customer.name,
          customerNo: customer.customerNo,
          contact: customer.contact,
          phone: customer.phone,
        },
        validUntil: SCENARIO_QUOTE_VALID_UNTIL,
        items: merged.items,
      };
    },
    undefined,
    true,
  );
  return { state: next, quoteId: stableQuoteId };
}

function createSubmittedQuote(
  state: CpqState,
  scenario: 'submissionSuccess' | 'submissionFailure' | 'archived',
  customer: CustomerCatalogRecord,
  device: QuoteSelectionDevice,
  outcome: 'success' | 'failure',
): CpqState {
  const created = createScenarioDraft(state, scenario, customer, device);
  let next = created.state;
  const quoteId = created.quoteId;
  if (next.quotes[quoteId]?.status === 'draft' && next.quotes[quoteId].dirty)
    next = saveDraft(next, quoteId);
  next = transitionQuote(next, quoteId, 'pending_submission', fieldValuePort);

  const quote = next.quotes[quoteId];
  if (!quote) throw new Error('本地提交演示报价不存在');
  const orderForm = emptyOrderConfirmationForm({
    orderMode: 'rental',
    customerName: quote.customer.name,
    customerPhone: quote.customer.phone,
    customerManager: '本地演示销售',
    rrzOrderNumber: `LOCAL-DEMO-${scenario.toUpperCase()}`,
    tenantName: '本地演示承租人',
    tenantIdNumber: 'DEMO-ID-0001',
    tenantPhone: '13900000002',
    shippingAddress: '本地演示地址（虚构数据）',
    rent: 1200,
    deposit: 0,
    debugType: 'none',
    rrzRegistrationPhone: quote.customer.phone,
    transferReason: '出租寄出',
    logisticsCompany: '顺丰云仓（顺丰快递）',
    warehouse: '顺丰云仓-深圳',
    orderType: '零售业务',
    liveCategory: '美妆护肤',
    orderNote: '本地演示表单，不会提交到后端。',
  });
  const errors = validateOrderConfirmationForm(orderForm);
  if (Object.keys(errors).length)
    throw new Error(
      `本地下单演示表单无效：${Object.values(errors).join('；')}`,
    );

  next = {
    ...next,
    quotes: {
      ...next.quotes,
      [quoteId]: {
        ...quote,
        orderConfirmation: createOrderConfirmationSnapshot(
          quote,
          orderForm,
          '2026-10-27T00:00:00.000Z',
        ),
      },
    },
  };
  const started = beginSubmission(next, quoteId, fieldValuePort);
  next = applySubmissionResult(
    started.state,
    quoteId,
    started.operationId,
    outcome,
  );
  if (scenario === 'archived')
    next = transitionQuote(next, quoteId, 'archived', fieldValuePort);
  return next;
}

function shareScenario(
  state: CpqState,
  scenario: 'sharedFeedback' | 'expiredLink' | 'revokedLink',
  customer: CustomerCatalogRecord,
  device: QuoteSelectionDevice,
): CpqState {
  const created = createScenarioDraft(state, scenario, customer, device);
  let next = created.state;
  const quoteId = created.quoteId;
  if (next.quotes[quoteId]?.status === 'draft') {
    if (next.quotes[quoteId].dirty) next = saveDraft(next, quoteId);
    next = transitionQuote(
      next,
      quoteId,
      'customer_confirmation',
      fieldValuePort,
    );
  }
  const quote = next.quotes[quoteId];
  if (!quote) throw new Error('本地分享演示报价不存在');
  const expiresAt =
    scenario === 'expiredLink'
      ? SCENARIO_EXPIRED_AT
      : '2099-12-31T23:59:59.000Z';
  next = publishNegotiation(next, quoteId, fieldValuePort, {
    customerId: customer.id,
    customerName: customer.name,
    customerPhone: customer.phone,
    allowSelection: false,
    expiresAt,
    snapshotPolicy: 'published-channel-v1',
  });
  const link = next.negotiationLinks?.find(
    (candidate) =>
      candidate.quoteMasterId === (quote.masterId ?? quote.quoteId),
  );
  if (!link) throw new Error('本地分享演示未创建固定链接');

  if (scenario === 'expiredLink') {
    next = setNegotiationLinkLifecycle(
      next,
      link.id,
      'expired',
      SCENARIO_EXPIRED_AT,
    );
  } else if (scenario === 'revokedLink') {
    next = setNegotiationLinkLifecycle(next, link.id, 'revoked');
  } else {
    const publication = next.publications?.find(
      (candidate) => candidate.id === link.currentPublicationId,
    );
    if (!publication) throw new Error('本地分享演示缺少已发布快照');
    const receiptItems = publication.itemsSnapshot.map((item) => ({ ...item }));
    next = submitShareReceipt(next, {
      shareId: link.id,
      linkId: link.id,
      publicationId: publication.id,
      companyOrName: customer.name,
      phone: customer.phone,
      note: '本地演示客户反馈：请调整设备数量后重新报价。',
      items: receiptItems,
      customerAction: 'request_change',
      createdAt: '2026-10-27T00:00:00.000Z',
    });
  }
  return next;
}

function preserveNavigation(
  state: CpqState,
  snapshot: NavigationSnapshot,
): CpqState {
  return {
    ...state,
    activeQuoteId: snapshot.activeQuoteId,
    activeQuoteVersionId: snapshot.activeQuoteVersionId,
    openQuoteIds: [...snapshot.openQuoteIds],
    activeView: snapshot.activeView,
    quoteManagementDetail: snapshot.quoteManagementDetail
      ? { ...snapshot.quoteManagementDetail }
      : undefined,
    currentRoleId: snapshot.currentRoleId,
    currentActorUserId: snapshot.currentActorUserId,
    role: snapshot.role,
  };
}

/**
 * Add editable, clearly labelled quote lifecycle samples to the browser-only
 * rehearsal. All lifecycle changes go through the existing state functions;
 * no request is sent to a backend or order system.
 */
export function seedLocalDemoScenarios(state: CpqState): CpqState {
  const missing = Object.entries(LOCAL_DEMO_SCENARIO_QUOTE_IDS).filter(
    ([, quoteId]) => !state.quotes[quoteId],
  ) as Array<[keyof typeof LOCAL_DEMO_SCENARIO_QUOTE_IDS, string]>;
  if (missing.length === 0) return state;

  const navigation: NavigationSnapshot = {
    activeQuoteId: state.activeQuoteId,
    activeQuoteVersionId: state.activeQuoteVersionId,
    openQuoteIds: [...state.openQuoteIds],
    activeView: state.activeView,
    quoteManagementDetail: state.quoteManagementDetail
      ? { ...state.quoteManagementDetail }
      : undefined,
    currentRoleId: state.currentRoleId,
    currentActorUserId: state.currentActorUserId,
    role: state.role,
  };
  let next = structuredClone(state);
  const customer = addScenarioCustomer(next);
  const device = selectScenarioDevice(next);
  next = switchToSeedAdministrator(next);

  if (!next.quotes[LOCAL_DEMO_SCENARIO_QUOTE_IDS.draft])
    next = createScenarioDraft(next, 'draft', customer, device).state;
  if (!next.quotes[LOCAL_DEMO_SCENARIO_QUOTE_IDS.sharedFeedback])
    next = shareScenario(next, 'sharedFeedback', customer, device);
  if (!next.quotes[LOCAL_DEMO_SCENARIO_QUOTE_IDS.expiredLink])
    next = shareScenario(next, 'expiredLink', customer, device);
  if (!next.quotes[LOCAL_DEMO_SCENARIO_QUOTE_IDS.revokedLink])
    next = shareScenario(next, 'revokedLink', customer, device);
  if (!next.quotes[LOCAL_DEMO_SCENARIO_QUOTE_IDS.submissionSuccess])
    next = createSubmittedQuote(
      next,
      'submissionSuccess',
      customer,
      device,
      'success',
    );
  if (!next.quotes[LOCAL_DEMO_SCENARIO_QUOTE_IDS.submissionFailure])
    next = createSubmittedQuote(
      next,
      'submissionFailure',
      customer,
      device,
      'failure',
    );
  if (!next.quotes[LOCAL_DEMO_SCENARIO_QUOTE_IDS.archived])
    next = createSubmittedQuote(next, 'archived', customer, device, 'success');

  const expiredLink = next.negotiationLinks?.find(
    (link) => link.quoteMasterId === LOCAL_DEMO_SCENARIO_QUOTE_IDS.expiredLink,
  );
  const expiredPublication = expiredLink?.currentPublicationId
    ? next.publications?.find(
        (publication) => publication.id === expiredLink.currentPublicationId,
      )
    : undefined;
  if (
    expiredLink &&
    expiredPublication &&
    !negotiationIsExpired(expiredLink, expiredPublication)
  )
    throw new Error('过期链接演示场景没有进入过期状态');

  return preserveNavigation(next, navigation);
}
